import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Pagination } from '@mui/material';
import moment from 'moment';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { useGetAuditLogsByGroupIdPaginated } from 'api/bitedb/audit/audit.hooks';

interface Props {
  groupId: string;
  isInteractionDisabled?: boolean;
}

const PAGE_SIZE = 20;

/**
 * Entity Hub Notifications tab — a consolidated, cross-database view of the
 * same "AuditEvent" activity each database's own Screening History tab shows
 * one database at a time. Styled after the Export Hub's Notifications tab
 * (eh-notif-* classes) since it's the same "day-grouped feed" shape.
 */
const EntityHubNotificationsTab: React.FC<Props> = ({
  groupId,
  isInteractionDisabled = false,
}) => {
  const history = useHistory();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useGetAuditLogsByGroupIdPaginated(
    groupId,
    PAGE_SIZE,
    (page - 1) * PAGE_SIZE,
    true,
    'people & companies',
  );

  const feed = data?.data || [];
  const totalPages = data?.pagination?.totalPages || 1;

  const groups: Array<{ day: string; items: typeof feed }> = [];
  feed.forEach((entry) => {
    const day = moment(entry.updated_at).format('YYYY-MM-DD');
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.items.push(entry);
    } else {
      groups.push({ day: day, items: [entry] });
    }
  });

  const dayLabel = (day: string): string => {
    const m = moment(day);
    if (m.isSame(moment(), 'day')) return 'Today';
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
    return m.format('MMMM D, YYYY');
  };

  const goToDatabase = (databaseId: string) => {
    if (isInteractionDisabled || !databaseId) {
      return;
    }
    history.push(`/database/${databaseId}?tab=Screening History`);
  };

  return (
    <div className='export-hub-panel' style={{ width: '100%' }}>
      <div className='eh-intg-intro'>
        <h2 className='eh-intg-intro-title'>
          <NotificationsNoneOutlinedIcon
            style={{ fontSize: 20, marginRight: 8, verticalAlign: 'middle' }}
          />
          Notifications
        </h2>
        <p className='eh-intg-intro-sub'>
          Everything happening across your entity databases — status changes,
          screenings run, and reviews resolved.
        </p>
      </div>

      {isLoading && feed.length === 0 ? (
        <div className='eh-table-skeleton'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      ) : feed.length === 0 ? (
        <div className='export-hub-empty'>
          <NotificationsNoneOutlinedIcon
            className='export-hub-empty-icon'
            style={{ fontSize: 32 }}
          />
          <div className='export-hub-empty-title'>You&rsquo;re all caught up</div>
          <div className='export-hub-empty-sub'>
            Activity across your entity databases will show up here.
          </div>
        </div>
      ) : (
        <>
          <div className='eh-notif-feed'>
            {groups.map((g) => (
              <div className='eh-notif-group' key={g.day}>
                <div className='eh-notif-day'>{dayLabel(g.day)}</div>
                {g.items.map((entry) => {
                  const isError = entry.log_level === 2;
                  return (
                    <button
                      key={entry.id}
                      type='button'
                      className='eh-notif-row'
                      onClick={() => goToDatabase(entry.database_id)}
                    >
                      <span
                        className={`eh-tl-icon ${
                          isError ? 'eh-tl-unlink' : 'eh-tl-screening'
                        }`}
                      >
                        {isError ? (
                          <WarningAmberIcon style={{ fontSize: 16 }} />
                        ) : (
                          <BoltOutlinedIcon style={{ fontSize: 16 }} />
                        )}
                      </span>
                      <span className='eh-notif-body'>
                        <span className='eh-notif-text'>
                          {entry.database_name && (
                            <strong>{entry.database_name}</strong>
                          )}
                          {entry.database_name ? ' — ' : ''}
                          {entry.message}
                        </span>
                        <span className='eh-notif-meta'>
                          {entry.user_name || 'System'} ·{' '}
                          {moment(entry.updated_at).fromNow()}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div
              className='hb-pagination'
              style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <Pagination
                onChange={(_e, newPage) => setPage(newPage)}
                size='large'
                count={totalPages}
                shape='rounded'
                variant='text'
                page={page}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EntityHubNotificationsTab;
